import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";

import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import moment from 'moment';

import { getNotificationsState } from '../../../features/communication/get_notifications';
import { MISC_DATE_ISO_FORMAT, MISC_DATE_POLISH_FORMAT, ROUTE_NOTIFICATIONS_NEW } from '../../../core/constants';
import { getAllPodsState } from '../../../features/pod/get_all_pods';
import HtmlTooltip from '../../common/html_tooltip';
import { DeleteButton, DuplicateButton } from '../../common/button';
import { deleteNotificationAction } from '../../../features/communication/delete_notification';
import { deleteNotificationNotificationData } from '../../../core/utils/notification_utils';
import useNotification from '../../../core/hooks/use_notification';
import { ApiRequestStatusEnum } from '../../../core/enums/common/api';

const NotificationsList = ({ refreshList, showCancelButton }) => {
    const { showNotification } = useNotification();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const getNotifications = useSelector(getNotificationsState);
    const getAllPods = useSelector(getAllPodsState);

    const [loadingCancelIds, setLoadingCancelIds] = useState([]);

    const getTooltipContent = (podIds) => {
        return (
            <React.Fragment>
                { podIds.map(podId => {
                    const foundPod = getAllPods?.data.find(pod => pod.id === podId);
                    if (!foundPod) return null;
                    return (
                        <Typography variant="body2" key={ podId } sx={ { mb: 0.5 } }>
                            { `${ foundPod.indoorPartner?.name } (${ foundPod.description }) - ${ foundPod.device?.serviceId || 'brak urządzenia' }` }
                        </Typography>
                    );
                }) }
            </React.Fragment>
        );
    };

    const cancelNotification = (id) => {
        setLoadingCancelIds(prev => [...prev, id]);
        dispatch(deleteNotificationAction(id))
            .then(response => {
                showNotification(deleteNotificationNotificationData(response));
                if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) refreshList();
            })
            .finally(() => {
                setLoadingCancelIds(prev => prev.filter(item => item !== id));
            });
    };

    return (
        <TableContainer component={ Paper } variant="outlined">
            <Table stickyHeader>
                <TableHead>
                    <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                        <TableCell>Data utworzenia</TableCell>
                        <TableCell>Data wysyłki</TableCell>
                        <TableCell>Typ notyfikacji</TableCell>
                        <TableCell align="center">Wysłano do wszystkich</TableCell>
                        <TableCell>Treść</TableCell>
                        <TableCell align="right" sx={ { minWidth: 120 } }></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    { getNotifications?.data?.content?.map(notificationRequest => (
                        <TableRow key={ notificationRequest?.id } hover>
                            <TableCell>
                                { moment(notificationRequest?.requestPlaced, MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_FORMAT) }
                            </TableCell>
                            <TableCell>
                                { moment(notificationRequest?.requestedRealization, MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_FORMAT) }
                            </TableCell>
                            <TableCell sx={ { fontWeight: 500 } }>
                                { notificationRequest?.notificationType }
                            </TableCell>
                            <TableCell align="center">
                                { notificationRequest?.podIds?.length === 0 ? (
                                    <Chip label="TAK" color="success" size="small" variant="outlined"/>
                                ) : (
                                    <HtmlTooltip title={ getTooltipContent(notificationRequest?.podIds) } arrow>
                                        <Chip
                                            label={ `NIE (${ notificationRequest?.podIds?.length })` }
                                            color="default"
                                            size="small"
                                            sx={ { cursor: 'help' } }
                                        />
                                    </HtmlTooltip>
                                ) }
                            </TableCell>
                            <TableCell>
                                <Box sx={ { minWidth: 280, maxWidth: 450 } }>
                                    { notificationRequest?.translatedMessages.map(message => (
                                        <Accordion
                                            key={ message.languageCode }
                                            disableGutters
                                            elevation={ 0 }
                                            square
                                            sx={ {
                                                border: theme => `1px solid ${ theme.palette.divider }`,
                                                borderBottom: 0,
                                                '&:last-of-type': { borderBottom: theme => `1px solid ${ theme.palette.divider }` },
                                                '&:before': { display: 'none' },
                                                backgroundColor: 'transparent'
                                            } }
                                        >
                                            <AccordionSummary
                                                expandIcon={ <ExpandMoreIcon fontSize="small"/> }
                                                sx={ { minHeight: 36, '& .MuiAccordionSummary-content': { my: 1 } } }
                                            >
                                                <Typography variant="body2" sx={ { fontWeight: 500 } }>
                                                    <b>{ message.languageCode }</b> - { message.title }
                                                </Typography>
                                            </AccordionSummary>
                                            <AccordionDetails sx={ { pt: 0, pb: 1.5, px: 2 } }>
                                                <Typography variant="caption" sx={ { wordBreak: 'break-word' } }>
                                                    { message.body }
                                                </Typography>
                                            </AccordionDetails>
                                        </Accordion>
                                    )) }
                                </Box>
                            </TableCell>
                            <TableCell align="right">
                                <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={ 1 }>
                                    <Box title="Duplikuj">
                                        <DuplicateButton
                                            onClick={ () => navigate(ROUTE_NOTIFICATIONS_NEW, { state: { notificationRequest } }) }/>
                                    </Box>

                                    { showCancelButton && (
                                        <Box title="Anuluj wysyłkę">
                                            <DeleteButton
                                                onDeleteClick={ () => cancelNotification(notificationRequest?.id) }
                                                isLoading={ loadingCancelIds.includes(notificationRequest?.id) }
                                            />
                                        </Box>
                                    ) }
                                </Stack>
                            </TableCell>
                        </TableRow>
                    )) }
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default NotificationsList;